import { UPLOADS_URL } from '../api/axios';
import React, { useEffect, useState } from 'react';
import API from '../api/axios';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';

import { API_URL } from '../api/axios';
const UPLOADS_BASE = API_URL;

const resolveImg = (src) => {
  if (!src) return src;
  if (src.startsWith('/frontend/')) return src.replace('/frontend/', '/');
  if (src.startsWith('http') || src.startsWith('/')) return src;
  return `${UPLOADS_BASE}/uploads/${src}`;
};

const DEFAULTS = {
  hero: { image: `${process.env.PUBLIC_URL}/images/page-banners/about-us.jpg_202608071713.jpeg` },
  intro: {
    kicker: 'School Introduction',
    heading: 'About Cambridge International Sr. Sec. School, Bhadana',
    paragraphs: [
      'Welcome to Cambridge International Sr. Sec. School, Bhadana. Nurturing Brilliance, Inspiring Excellence.',
      "We are thrilled to introduce our seventh (7th) branch, Cambridge International Sr. Sec. School, Bhadana, building on a legacy of excellence across six thriving campuses in India. Here, we are dedicated to more than just academics; we are committed to creating a supportive and enriching environment where every child can flourish intellectually, emotionally, socially, and physically. We believe in the boundless potential of every student, and it is our privilege to guide them on this exciting journey of growth and discovery.",
      'Our mission is to empower every child with knowledge, confidence and compassion, enabling them to become thoughtful, responsible individuals who make a meaningful difference in the world.',
    ].join('\n'),
    quote: '"Education is the most powerful weapon which you can use to change the world." – Nelson Mandela',
  },
  visionMission: {
    vision_heading: 'Our Vision',
    vision_intro: "At Cambridge International Sr. Sec. School, we envision a transformative educational experience that empowers every learner to excel both academically and personally. Our commitment is to create a vibrant community that fosters:",
    vision_points: [
      'Curiosity and Discovery: Encouraging students to explore their interests and engage in innovative learning experiences that ignite their passion for knowledge.',
      'Inclusivity and Diversity: Celebrating and respecting the unique backgrounds and perspectives of every individual, ensuring that all voices are heard and valued.',
      'Critical Thinking and Creativity: Cultivating essential skills that enable students to analyze, problem-solve and think outside the box, preparing them for real-world challenges.',
      'Character & Personality Development: Instilling core values such as integrity, empathy and resilience, nurturing well-rounded individuals who contribute positively to society.',
      'Lifelong Learning: Inspiring a love for learning that extends beyond the classroom, equipping students with the tools they need to adapt and thrive in an ever-evolving world.',
    ].join('\n'),
    vision_closing: 'Together, we strive to create an enriching environment that prepares our students not just for academic success, but for meaningful lives as engaged citizens.',
    mission_heading: 'Our Mission',
    mission_points: [
      'Empower Every Student: Inspire each child to realize their full potential with curiosity and confidence.',
      'Create an Inclusive Community: Foster a respectful, diverse environment where every voice is valued.',
      'Cultivate Critical Thinkers: Equip students with skills for adaptability, creativity and problem-solving.',
      'Instil Integrity and Compassion: Build character through values of empathy, honesty and responsibility.',
      'Promote Lifelong Learning: Encourage a love for knowledge that extends beyond the classroom.',
    ].join('\n'),
    mission_closing: 'Through our dedication to these values, we aspire to nurture a generation of confident, empathetic and capable leaders.',
  },
  values: {
    kicker: 'Core Values',
    heading: 'Our Core Values',
    intro: 'We are guided by a set of core values that reflect our commitment to creating a positive and enriching educational environment for all. These values inspire our actions and shape our community.',
    items: [
      { icon: 'fa-shield-heart', text: 'Integrity: We are committed to maintaining honesty and accountability in all our interactions.' },
      { icon: 'fa-handshake', text: 'Respect: We cultivate an inclusive environment that values mutual respect among all individuals.' },
      { icon: 'fa-star', text: 'Excellence: We strive to achieve the highest standards in both teaching and learning.' },
      { icon: 'fa-users', text: 'Collaboration: We encourage teamwork and foster partnerships within our community.' },
      { icon: 'fa-lightbulb', text: 'Innovation: We embrace creativity and welcome new ideas to enhance the educational experience.' },
      { icon: 'fa-heart', text: 'Compassion: We prioritize the emotional and social well-being of every student, providing support and understanding.' },
      { icon: 'fa-book-open', text: 'Lifelong Learning: We promote a culture of continuous growth and a genuine curiosity for knowledge.' },
    ],
  },
};

const splitLines = (text) => String(text || '').split('\n').map((p) => p.trim()).filter(Boolean);

const splitPoints = (text) =>
  splitLines(text).map((p) => {
    const idx = p.indexOf(':');
    if (idx > 0) return { label: p.slice(0, idx), rest: p.slice(idx + 1).trim() };
    return { label: null, rest: p };
  });

const About = () => {
  const [sectionData, setSectionData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await API.get('/home-sections');
        if (res.data.data) setSectionData(res.data.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const hero = sectionData.about_hero || DEFAULTS.hero;
  const heroImage = hero.image ? resolveImg(hero.image) : resolveImg(DEFAULTS.hero.image);

  const intro = { ...DEFAULTS.intro, ...(sectionData.about_intro || {}) };
  const introParagraphs = intro.paragraphs ? splitLines(intro.paragraphs) : splitLines(DEFAULTS.intro.paragraphs);

  

  

  return (
    <>
      <SEO
        title="About Cambridge International Sr. Sec. School, Bhadana"
        description="About Cambridge International Sr. Sec. School, Bhadana — nurturing brilliance, inspiring excellence."
        keywords="About Cambridge International Sr. Sec. School, Cambridge International Sr. Sec. School Bhadana, CBSE School Bhadana"
      />

            {/* HERO */}
      <PageBanner
        title={hero.heading || "About Us"}
        subtitle={hero.sub_heading || ""}
        backgroundImage={heroImage}
        loading={loading}
      />

      {/* SCHOOL INTRO */}
      <section className="section bg-white">
        <div className="container">
          <div className="w-full">
            <Reveal>
              <div className="kicker text-center">{intro.kicker}</div>
              <h2 className="h-section text-3xl md:text-4xl lg:text-5xl font-bold mt-3 text-center">{intro.heading}</h2>
              <div className="divider-line mx-auto mt-5" />
            </Reveal>

            <div className="mt-8 space-y-6 text-body text-[17px] leading-relaxed">
              <Reveal delayMs={70}>
                <div className="prose max-w-none text-body" dangerouslySetInnerHTML={{ __html: intro.paragraphs || DEFAULTS.intro.paragraphs }} />
              </Reveal>
              
            </div>
          </div>
        </div>
      </section>

      

      
    </>
  );
};

export default About;
